import { Modal, message } from "antd";
import type { CustomerVo } from "@zhihaoscm/entity";
import { configureRequestRuntime, readToken, USER_CONTEXT_KEY } from "@/api/runtime";
import { customerService } from "@/api/customerService";
import { loginService } from "@/api/loginService";
import { initializeTenant, runtimePublicConfig } from "@/runtime/config";
import { initializeTelemetry, registerTelemetryUser } from "@/runtime/telemetry";
import { hasRuntimeMemberPermission, useAccountSession, useUserSession } from "@/stores/session";

/** 本地跨项目 URL Token 注入，条件与 Vue 路由守卫一致。 */
export function injectLocalToken(search = window.location.search) {
    if (window.location.protocol !== "http:") return;
    const token = new URLSearchParams(search).get("token");
    if (!token) return;
    let context: Record<string, unknown> = {};
    try { context = JSON.parse(localStorage.getItem(USER_CONTEXT_KEY) || "{}"); }
    catch { context = {}; }
    localStorage.setItem(USER_CONTEXT_KEY, JSON.stringify({ ...context, zhihaoWebToken: token }));
    // 外部项目可能刚写入完整用户上下文；整体同步可避免内存旧值回写覆盖其它字段。
    useUserSession.setState({ ...context, zhihaoWebToken: token } as any);
}

/** 拉取用户及脱敏字段，严格保持 Vue getUserInfo 的请求顺序和映射。 */
export async function refreshUserInfo() {
    const data = await loginService.getMe();
    data.actualAccount.realName = await customerService.getCustomerRealName(data.actualAccount.id);
    data.actualAccount.mobile = await customerService.getCustomerRealMobile(data.actualAccount.id);
    const userInfo = {
        customer: data.actualAccount,
        permissions: data.permissions,
        memberPermission: data.actualMemberPermission,
        proxyAccount: data.proxyAccount,
        proxyMemberPermission: data.proxyMemberPermission
    } as unknown as CustomerVo;
    useUserSession.getState().setUser(userInfo, {
        authorizationState: data.authorizationState,
        bindTime: data.bindTime ?? "",
        email: data.actualAccount.email ?? "",
        mobile: data.actualAccount.mobile ?? "",
        nickName: data.nickName ?? "",
        passwordUpdateTime: data.actualAccount.passwordUpdateTime ?? ""
    });
    useAccountSession.getState().setFromUser(userInfo);
    registerTelemetryUser(userInfo.customer.id);
}

function confirmMessage(content: string) {
    return new Promise<void>((resolve, reject) => {
        Modal.confirm({ title: "提示", content, onOk: () => resolve(), onCancel: () => reject(new Error("cancelled")) });
    });
}

/** 连接请求层、Store、租户和埋点，并在首屏前恢复登录用户。 */
export async function bootstrapApplication() {
    injectLocalToken();
    configureRequestRuntime({
        confirm: confirmMessage,
        error: (content) => { void message.error(content); },
        hasMemberPermission: hasRuntimeMemberPermission,
        loginExpired: () => { window.location.href = window.location.protocol === "http:" ? "http://localhost:5173/login" : `${window.location.origin}/login`; },
        offline: () => { window.location.href = "/console/500"; },
        success: (content) => { void message.success(content); },
        tenantId: () => runtimePublicConfig.tenantId,
        warning: (content) => { void message.warning(content); }
    });
    await initializeTenant();
    initializeTelemetry();
    if (readToken()) await refreshUserInfo();
}
